import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PageTransition, JellyButton } from "../components/common";
import usePageTitle from "../hooks/usePageTitle";
import api from "../utils/api";

const LoginPage = ({ initialMode }) => {
  usePageTitle(initialMode === "register" ? "Create Account - Start Your Story" : "Login to Your Account");
  const [isLogin, setIsLogin] = useState(initialMode !== "register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Referral and affiliate tracking
  const [referralCode, setReferralCode] = useState("");
  const [affiliateCode, setAffiliateCode] = useState("");
  const [referralBonus, setReferralBonus] = useState(null);
  
  useEffect(() => {
    // Check for referral code in URL (?ref=CODE)
    const refCode = searchParams.get("ref");
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      validateReferralCode(refCode);
    }
    
    // Check for affiliate code in URL (?aff=CODE)
    const affCode = searchParams.get("aff");
    if (affCode) {
      setAffiliateCode(affCode.toUpperCase());
      // Track affiliate click
      trackAffiliateClick(affCode);
    }
  }, [searchParams]);
  
  const validateReferralCode = async (code) => {
    try {
      const res = await api.post("/referrals/validate-code", { referral_code: code });
      if (res.data.valid) {
        setReferralBonus(res.data.welcome_bonus);
        toast.success(`Referral code applied! You'll get ${res.data.welcome_bonus} bonus credits! 🎉`, {
          duration: 5000
        });
      }
    } catch (err) {
      console.error("Invalid referral code:", err);
      setReferralCode("");
    }
  };
  
  const trackAffiliateClick = async (code) => {
    try {
      await api.get(`/affiliates/track/${code}`);
    } catch (err) {
      console.error("Failed to track affiliate:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Welcome back!");
        navigate("/dashboard");
      } else {
        // Include referral and affiliate codes in registration
        const result = await register(email, password, name, referralCode, affiliateCode);
        const bonusMessage = referralBonus 
          ? ` You've earned ${referralBonus} bonus credits!` 
          : "";
        toast.success(`Account created!${bonusMessage} Please check your email to verify your account.`, {
          duration: 5000
        });
        // Redirect to verify email page with instructions
        navigate("/verify-email");
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail || "";
      const statusCode = err.response?.status;
      
      // Provide user-friendly error messages
      if (statusCode === 401 || errorDetail.toLowerCase().includes("invalid credentials")) {
        toast.error("Incorrect email or password. Please try again.");
      } else if (statusCode === 403) {
        toast.error(errorDetail || "Your account has been suspended. Please contact support.");
      } else if (statusCode === 400 && errorDetail.toLowerCase().includes("email already registered")) {
        toast.error("This email is already registered. Try signing in instead.");
      } else if (statusCode === 400) {
        toast.error(errorDetail || "Please check your information and try again.");
      } else {
        toast.error(errorDetail || "Something went wrong. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Include referral/affiliate codes in the redirect URL
    let redirectUrl = window.location.origin + '/dashboard';
    if (referralCode) {
      redirectUrl += `?ref=${referralCode}`;
    }
    if (affiliateCode) {
      redirectUrl += (referralCode ? '&' : '?') + `aff=${affiliateCode}`;
    }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <PageTransition>
      <div className="min-h-screen hero-gradient pt-24 pb-16 px-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-violet-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                {isLogin ? "Welcome Back!" : "Join the Magic"}
              </h1>
              <p className="text-slate-600 mt-2">
                {isLogin ? "Sign in to continue your adventure" : "Create an account to start storytelling"}
              </p>
            </div>
            
            {/* Referral Bonus Banner */}
            {!isLogin && referralBonus && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 bg-gradient-to-r from-violet-500 to-pink-500 rounded-xl p-4 text-white"
              >
                <div className="flex items-center gap-3">
                  <Gift className="w-6 h-6 flex-shrink-0" />
                  <div>
                    <p className="font-bold">You've been invited!</p>
                    <p className="text-sm text-violet-100">
                      Sign up now to get {referralBonus} bonus credits free!
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-magic w-full"
                    placeholder="Your name"
                    required={!isLogin}
                    data-testid="register-name-input"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-magic w-full"
                  placeholder="you@example.com"
                  required
                  data-testid="email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-magic w-full"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>

              <JellyButton
                type="submit"
                variant="primary"
                className="w-full"
                disabled={loading}
                data-testid="auth-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (isLogin ? "Sign In" : "Create Account")}
              </JellyButton>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">or continue with</span>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              className="w-full h-12 rounded-xl border-2 border-slate-200 flex items-center justify-center gap-3 hover:bg-slate-50 transition-colors"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="font-medium text-slate-700">Google</span>
            </button>

            <p className="text-center text-slate-600 mt-6">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-violet-600 font-medium hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    </PageTransition>
  );
};

export default LoginPage;
